import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { DateTime } from 'luxon';
import { take } from 'rxjs';
import { HourBankEntryType, UserHourBankModel } from 'app/models/user_hour_bank.model';
import { UserHourBankService } from 'app/services/user_hour_bank.service';
import { ToastService } from 'app/shared/services/toast.service';
import { ApiService } from 'app/shared/apis/api.service';
import { UserModel } from 'app/models/user.model';
import { ModuleAttachmentsComponent } from 'app/shared/components/module-attachments/module-attachments.component';

export type UserHourBankFormDialogData = {
    item?: UserHourBankModel | null;
    user_id: number;
};

type HourForm = FormGroup<{
    entry_date: FormControl<DateTime | Date | string | null>;
    hours: FormControl<string | number | null>;
    entry_type: FormControl<HourBankEntryType | null>;
    observation: FormControl<string | null>;
}>;

@Component({
    selector: 'app-user-hour-bank-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatInputModule,
        MatIconModule,
        MatSelectModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        NgxMaskDirective,
        ModuleAttachmentsComponent,
    ],
    providers: [provideNgxMask()],
    templateUrl: './user-hour-bank-form.component.html',
})
export class UserHourBankFormComponent implements OnInit {
    @ViewChild(ModuleAttachmentsComponent) attachmentsCmp?: ModuleAttachmentsComponent;

    form: HourForm = this.fb.group({
        entry_date: [null as DateTime | Date | string | null, Validators.required],
        hours: [null as string | number | null, Validators.required],
        entry_type: ['CREDIT' as HourBankEntryType, Validators.required],
        observation: [null as string | null],
    });

    loading = false;
    loggedUser: UserModel | null = null;
    currentItem: UserHourBankModel | null = null;
    entryTypeOptions: { value: HourBankEntryType; label: string }[] = [
        { value: 'CREDIT', label: 'Crédito' },
        { value: 'DEBIT', label: 'Débito' },
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: UserHourBankFormDialogData,
        private dialogRef: MatDialogRef<UserHourBankFormComponent>,
        private fb: FormBuilder,
        private service: UserHourBankService,
        private toast: ToastService,
    ) {
        this.dialogRef.addPanelClass('card-dialog');
    }

    ngOnInit(): void {
        ApiService.userCurrent.pipe(take(1)).subscribe((u) => (this.loggedUser = u));
        this.currentItem = this.data?.item ? new UserHourBankModel(this.data.item) : null;
        if (this.currentItem?.user_hour_bank_id) {
            this.form.patchValue({
                entry_date: this.toDateTime(this.currentItem.entry_date),
                hours: this.currentItem.hours,
                entry_type: this.currentItem.entry_type,
                observation: this.currentItem.observation,
            });
        }
    }

    get isEdit(): boolean {
        return !!this.currentItem?.user_hour_bank_id;
    }

    get entityId(): number | null {
        return this.currentItem?.user_hour_bank_id ?? null;
    }

    private toDateTime(v: Date | string | null | undefined): DateTime | null {
        if (!v) return null;
        if (DateTime.isDateTime(v)) return v as DateTime;
        if (v instanceof Date) return DateTime.fromJSDate(v);
        if (typeof v === 'string') {
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return DateTime.fromFormat(v, 'dd/MM/yyyy');
            const iso = DateTime.fromISO(v.slice(0, 10));
            return iso.isValid ? iso : null;
        }
        return null;
    }

    private toIsoDate(v: DateTime | Date | string | null): string | null {
        if (!v) return null;
        if (typeof v === 'string') {
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(v.trim())) {
                const dt = DateTime.fromFormat(v.trim(), 'dd/MM/yyyy');
                return dt.isValid ? dt.toISODate() : null;
            }
            if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
            return null;
        }
        if (DateTime.isDateTime(v)) return v.isValid ? v.toISODate() : null;
        if (v instanceof Date) return DateTime.fromJSDate(v).toISODate();
        return null;
    }

    private parseDecimal(v: string | number | null): number {
        if (v == null || v === '') return NaN;
        if (typeof v === 'number') return v;
        return Number(String(v).replace(/\./g, '').replace(',', '.'));
    }

    clear(): void {
        this.dialogRef.close(false);
    }

    done(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const value = this.form.getRawValue();
        const entryIso = this.toIsoDate(value.entry_date);
        if (!entryIso) {
            this.toast.error('Data inválida.');
            return;
        }
        const hours = Math.abs(this.parseDecimal(value.hours));
        if (!Number.isFinite(hours) || hours <= 0) {
            this.toast.error('Horas inválidas.');
            return;
        }

        this.loading = true;
        const payload: any = {
            user_id: this.data.user_id,
            entry_date: entryIso,
            hours,
            entry_type: value.entry_type,
            observation: value.observation,
            created_by_user_id: this.loggedUser?.user_id ?? null,
        };

        const wasEdit = this.isEdit;
        const req$ = wasEdit
            ? this.service.update({ ...this.currentItem, ...payload } as any)
            : this.service.create(payload);

        req$.pipe(take(1)).subscribe({
            next: async (saved) => {
                try {
                    const item = new UserHourBankModel(saved as any);
                    this.currentItem = item;
                    const id = item.user_hour_bank_id;
                    if (id && this.attachmentsCmp?.hasChanges) {
                        await this.attachmentsCmp.saveAttachments(id, { silent: true });
                    }
                    this.toast.success(wasEdit ? 'Lançamento atualizado.' : 'Lançamento criado.');
                    this.dialogRef.close(true);
                } catch {
                    // Erro de anexo já toastado; mantém dialog aberto
                } finally {
                    this.loading = false;
                }
            },
            error: (err) => {
                this.loading = false;
                this.toast.error(err?.error?.message || 'Erro ao salvar.');
            },
        });
    }
}
